import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2 } from "lucide-react";

type RecipientPhoneCsvUploadProps = {
  uploadedCount: number;
  matchedCount: number;
  skippedCount: number;
  isParsing: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
};

export function RecipientPhoneCsvUpload({
  uploadedCount,
  matchedCount,
  skippedCount,
  isParsing,
  onUpload,
  onClear,
}: RecipientPhoneCsvUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasUpload = uploadedCount > 0;

  return (
    <div className="space-y-2 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <p className="text-sm font-medium">Phone list (CSV)</p>
          <p className="text-xs text-muted-foreground">
            Upload a CSV with phone numbers to choose who receives this broadcast. Matching
            recipients are selected; everyone else is deselected.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isParsing}
            onClick={() => inputRef.current?.click()}
          >
            {isParsing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Parsing...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {hasUpload ? "Replace CSV" : "Upload CSV"}
              </>
            )}
          </Button>
          {hasUpload ? (
            <Button type="button" variant="ghost" size="sm" onClick={onClear}>
              <X className="h-4 w-4 mr-2" />
              Clear
            </Button>
          ) : null}
        </div>
      </div>
      {hasUpload ? (
        <p className="text-sm text-muted-foreground">
          {uploadedCount.toLocaleString()} phone number
          {uploadedCount !== 1 ? "s" : ""} in CSV
          {" · "}
          {matchedCount.toLocaleString()} matched recipient
          {matchedCount !== 1 ? "s" : ""}
          {skippedCount > 0
            ? ` · ${skippedCount.toLocaleString()} row${skippedCount !== 1 ? "s" : ""} skipped (no valid phone)`
            : ""}
        </p>
      ) : null}
    </div>
  );
}
